<template>
  <div class="lp">
    <!-- Futuristic background -->
    <div class="lp-bg">
      <div class="lp-bg-img"></div>
      <div class="lp-bg-mesh"></div>
      <div class="lp-bg-grid"></div>
      <div class="lp-bg-overlay"></div>
      <!-- Floating orbs -->
      <div class="lp-orb lp-orb-1"></div>
      <div class="lp-orb lp-orb-2"></div>
      <div class="lp-orb lp-orb-3"></div>
      <div class="lp-orb lp-orb-4"></div>
      <!-- Accent lines -->
      <div class="lp-line lp-line-1"></div>
      <div class="lp-line lp-line-2"></div>
    </div>

    <!-- Form container -->
    <div class="lp-form-overlay">
      <div class="lp-form-card">
        <div class="lp-form-inner">
          <!-- Logo -->
          <div class="lp-logo-wrap">
            <img :src="logoSrc" alt="Logo" class="lp-logo-img" />
            <div class="lp-logo-glow"></div>
          </div>

          <h1 class="lp-title">{{ msg('Welcome') }}</h1>
          <p class="lp-subtitle">{{ msg('Please sign-in to your account') }}</p>

          <div v-if="!isloggedIn">
            <validation-observer ref="loginForm" #default="{ invalid }">
              <b-form class="lp-form" @submit.prevent>

                <div class="lp-field">
                  <validation-provider #default="{ errors }" :name="msg('username')" rules="required">
                    <div class="lp-input-wrap" :class="{ 'lp-input-wrap--error': errors.length }">
                      <label class="lp-input-label">{{ msg('Username') }}</label>
                      <b-form-input
                        id="username"
                        v-model="username"
                        name="login-username"
                        :state="errors.length > 0 ? false : null"
                        :placeholder="msg('Username')"
                        class="lp-input"
                        autofocus
                      />
                      <span class="lp-input-line"></span>
                    </div>
                    <small v-if="errors[0]" class="lp-error">{{ errors[0] }}</small>
                  </validation-provider>
                </div>

                <div class="lp-field">
                  <validation-provider #default="{ errors }" :name="msg('Password')" rules="required">
                    <div class="lp-input-wrap" :class="{ 'lp-input-wrap--error': errors.length }">
                      <label class="lp-input-label">{{ msg('Password') }}</label>
                      <b-form-input
                        id="password"
                        v-model="password"
                        :type="passwordFieldType"
                        :state="errors.length > 0 ? false : null"
                        name="login-password"
                        :placeholder="msg('Password')"
                        class="lp-input lp-input--pw"
                      />
                      <feather-icon
                        class="lp-eye"
                        :icon="passwordToggleIcon"
                        @click="togglePasswordVisibility"
                      />
                      <span class="lp-input-line"></span>
                    </div>
                    <small v-if="errors[0]" class="lp-error">{{ errors[0] }}</small>
                  </validation-provider>
                </div>

                <b-button
                  variant="primary"
                  type="submit"
                  block
                  :disabled="invalid || processing"
                  class="lp-btn"
                  @click="submitLogin"
                >
                  <span class="lp-btn-text">
                    <b-spinner v-if="processing" small type="grow" class="mr-2"></b-spinner>
                    {{ msg('Sign in') }}
                  </span>
                  <span class="lp-btn-shine"></span>
                </b-button>
              </b-form>
            </validation-observer>
          </div>

          <div v-else class="lp-redirect">
            <div class="lp-redirect-spinner"></div>
            <p>{{ msg('Redirect to your account space in 5 seconds') }}</p>
          </div>

          <div class="lp-footer">
            <span>&copy; {{ new Date().getFullYear() }} Treasure Home</span>
          </div>
        </div>
      </div>
    </div>

    <b-modal id="overdue-invoice-modal" title="Overdue Invoices" hide-footer size="sm">
      <p class="my-4">
        You have overdue invoices that are not paid.<br />
        Payment links will be emailed to your registered address.
      </p>
      <template #modal-footer>
        <b-button variant="primary" @click="$bvModal.hide('overdue-invoice-modal')">OK</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { router } from '@/router'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      username: '',
      password: '',
      status: '',
      invalid: false,
      processing: false,
    }
  },
  computed: {
    ...mapGetters('user', ['isloggedIn']),
    logoSrc() {
      return require('@/assets/images/logo/treasure-home-logo.png')
    },
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  mounted() {
    this.setLanguage(null)
    this.redirect('/')
  },
  methods: {
    ...mapActions('user', ['login']),
    ...mapMutations('translator', ['setLanguage']),
    ...mapActions('notifications', { notifyError: 'error' }),
    submitLogin() {
      this.processing = true
      this.login({ username: this.username, password: this.password })
        .then(redirect => {
          this.redirect(redirect)
        })
        .catch(e => {
          if (e === 'overdue') {
            this.$bvModal.show('overdue-invoice-modal')
          } else {
            this.notifyError({ title: 'Login Error', message: e })
          }
        })
        .finally(() => {
          this.processing = false
        })
    },
    redirect(defaultRedirect) {
      const url = new URL(window.location.href)
      let returnUrl = url.searchParams.get('returnUrl')
      if (returnUrl != null && returnUrl !== '') {
        returnUrl = returnUrl.trim()
        returnUrl = returnUrl.replace(/https?:\/\//g, '').replace(/:\/\//g, '')
        returnUrl = returnUrl.replace(/\./g, '')
        router.push(returnUrl)
      } else {
        router.push(defaultRedirect)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

$gold: #c49b4a;
$gold-dim: rgba(196, 155, 74, 0.4);
$gold-glow: rgba(196, 155, 74, 0.25);
$blue-1: #0f1729;
$blue-2: #16203a;
$blue-3: #1e2d4a;
$blue-4: #2a3a5c;
$blue-glow: rgba(99, 149, 237, 0.2);
$blue-accent: rgba(147, 197, 253, 0.15);
$glass: rgba(255, 255, 255, 0.05);
$glass-border: rgba(255, 255, 255, 0.1);
$text: #e8ecf4;
$text-muted: #94a3b8;
$text-placeholder: #64748b;

.lp {
  position: relative;
  min-height: 100vh;
  min-height: calc(var(--vh, 1vh) * 100);
  font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  overflow: hidden;
}

/* ---------- Background ---------- */
.lp-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
}

.lp-bg-img {
  position: absolute;
  inset: 0;
  background-image: url('https://treasurehome.at/uploads/images/loginPage.jpeg');
  background-size: cover;
  background-position: center;
  opacity: 0.55;
  filter: saturate(1.1) brightness(0.7) contrast(1.05);
  transform: scale(1.05);
  transition: transform 12s ease, opacity 8s ease;
}

.lp:hover .lp-bg-img {
  transform: scale(1.08);
  opacity: 0.65;
}

.lp-bg-mesh {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 90% 60% at 15% 30%, rgba(99, 149, 237, 0.25) 0%, transparent 50%),
    radial-gradient(ellipse 70% 80% at 85% 25%, rgba(196, 155, 74, 0.2) 0%, transparent 50%),
    radial-gradient(ellipse 60% 70% at 50% 95%, rgba(139, 92, 246, 0.18) 0%, transparent 55%),
    radial-gradient(ellipse 50% 50% at 75% 70%, rgba(34, 211, 238, 0.12) 0%, transparent 60%),
    linear-gradient(180deg, rgba(15, 23, 41, 0.85) 0%, rgba(30, 58, 95, 0.6) 40%, rgba(22, 32, 58, 0.5) 100%);
}

.lp-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(148, 163, 184, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(148, 163, 184, 0.06) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, black 20%, transparent 70%);
}

.lp-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 41, 0.35) 0%,
    transparent 30%,
    transparent 70%,
    rgba(15, 23, 41, 0.4) 100%
  );
}

/* Floating orbs */
.lp-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.5;
  animation: lpFloat 20s ease-in-out infinite;
}

.lp-orb-1 {
  width: 320px;
  height: 320px;
  background: rgba(99, 149, 237, 0.35);
  top: 10%;
  left: 15%;
  animation-delay: 0s;
}

.lp-orb-2 {
  width: 220px;
  height: 220px;
  background: rgba(196, 155, 74, 0.3);
  bottom: 15%;
  right: 18%;
  animation-delay: -7s;
}

.lp-orb-3 {
  width: 180px;
  height: 180px;
  background: rgba(139, 92, 246, 0.25);
  top: 55%;
  left: 5%;
  animation-delay: -14s;
}

.lp-orb-4 {
  width: 160px;
  height: 160px;
  background: rgba(34, 211, 238, 0.2);
  top: 20%;
  right: 10%;
  animation-delay: -4s;
}

@keyframes lpFloat {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -20px) scale(1.05); }
  66% { transform: translate(-20px, 25px) scale(0.95); }
}

/* Accent lines */
.lp-line {
  position: absolute;
  background: linear-gradient(90deg, transparent, rgba(147, 197, 253, 0.5), rgba(196, 155, 74, 0.4), transparent);
  opacity: 0.6;
  height: 1px;
  width: 40%;
}

.lp-line-1 {
  top: 25%;
  left: 0;
  transform: rotate(-15deg);
}

.lp-line-2 {
  bottom: 30%;
  right: 0;
  transform: rotate(15deg);
}

/* ---------- Form card ---------- */
.lp-form-overlay {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  animation: lpSlideUp 0.6s ease-out;
}

@keyframes lpSlideUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.lp-form-card {
  position: relative;
  background: rgba(22, 32, 58, 0.7);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-radius: 20px;
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.2) inset,
    0 24px 64px rgba(15, 23, 41, 0.5),
    0 0 80px rgba(99, 149, 237, 0.08),
    0 0 40px rgba(196, 155, 74, 0.04);
  overflow: hidden;
}

.lp-form-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(147, 197, 253, 0.5), $gold-dim, transparent);
  opacity: 0.7;
}

.lp-form-inner {
  position: relative;
  padding: 2.5rem 2rem;
}

/* ---------- Logo ---------- */
.lp-logo-wrap {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
}

.lp-logo-img {
  position: relative;
  z-index: 1;
  height: 96px;
  width: auto;
  max-width: 320px;
  object-fit: contain;
  filter: drop-shadow(0 0 20px $gold-glow);
}

.lp-logo-glow {
  position: absolute;
  width: 140%;
  height: 130px;
  background: radial-gradient(ellipse at center, $gold-glow 0%, transparent 70%);
  filter: blur(20px);
  opacity: 0.6;
}

/* ---------- Typography ---------- */
.lp-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: $text;
  margin: 0 0 0.35rem;
  letter-spacing: -0.04em;
  line-height: 1.2;
}

.lp-subtitle {
  font-size: 0.9rem;
  color: $text-muted;
  margin: 0 0 1.75rem;
  font-weight: 500;
}

/* ---------- Form ---------- */
.lp-form {
  margin: 0;
}

.lp-field {
  margin-bottom: 1.5rem;
}

.lp-input-wrap {
  position: relative;
  padding-top: 1.25rem;
}

.lp-input-label {
  position: absolute;
  top: 0;
  left: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: $text-muted;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  transition: color 0.2s ease;
}

.lp-input-wrap:focus-within .lp-input-label {
  color: #93c5fd;
}

.lp-input-wrap--error .lp-input-label {
  color: #f87171;
}

.lp-input {
  width: 100%;
  background: $glass !important;
  border: 1px solid $glass-border !important;
  border-radius: 10px !important;
  padding: 0.85rem 1rem 0.85rem 1rem !important;
  padding-right: 2.75rem !important;
  font-size: 0.95rem !important;
  font-weight: 500 !important;
  color: $text !important;
  transition: all 0.25s ease !important;
  box-shadow: none !important;

  &::placeholder {
    color: $text-placeholder;
  }

  &:focus {
    outline: none !important;
    border-color: rgba(147, 197, 253, 0.5) !important;
    box-shadow: 0 0 0 3px rgba(99, 149, 237, 0.15) !important;
  }
}

.lp-input--pw {
  padding-right: 2.75rem !important;
}

.lp-input-line {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, rgba(147, 197, 253, 0.8), $gold);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.3s ease;
}

.lp-input-wrap:focus-within .lp-input-line {
  transform: scaleX(1);
}

.lp-eye {
  position: absolute;
  right: 1rem;
  bottom: 0.9rem;
  color: $text-muted;
  cursor: pointer;
  width: 18px;
  height: 18px;
  transition: color 0.2s ease;
}

.lp-eye:hover {
  color: $gold;
}

.lp-error {
  display: block;
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: #f87171;
  font-weight: 500;
}

/* ---------- Button ---------- */
.lp-btn {
  position: relative;
  margin-top: 0.5rem !important;
  padding: 1rem 1.5rem !important;
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  border-radius: 12px !important;
  border: none !important;
  color: #0a0a0f !important;
  background: linear-gradient(135deg, #e8c76a 0%, $gold 50%, #b8933a 100%) !important;
  box-shadow:
    0 4px 16px rgba(196, 155, 74, 0.35),
    inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
  overflow: hidden !important;
  transition: all 0.25s ease !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  letter-spacing: 0.04em;
}

.lp-btn-text {
  position: relative;
  z-index: 1;
}

.lp-btn-shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    105deg,
    transparent 40%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 60%
  );
  transform: translateX(-100%);
  transition: transform 0.6s ease;
}

.lp-btn:hover:not(:disabled) .lp-btn-shine {
  transform: translateX(100%);
}

.lp-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow:
    0 8px 28px rgba(196, 155, 74, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.35) !important;
}

.lp-btn:active:not(:disabled) {
  transform: translateY(0);
}

.lp-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---------- Redirect ---------- */
.lp-redirect {
  text-align: center;
  padding: 1.5rem 0;
}

.lp-redirect p {
  color: $text-muted;
  margin-top: 1rem;
  font-size: 0.9rem;
}

.lp-redirect-spinner {
  width: 36px;
  height: 36px;
  margin: 0 auto;
  border: 3px solid $blue-4;
  border-top-color: $gold;
  border-radius: 50%;
  animation: lpSpin 0.7s linear infinite;
}

@keyframes lpSpin {
  to { transform: rotate(360deg); }
}

/* ---------- Footer ---------- */
.lp-footer {
  margin-top: 2rem;
  text-align: center;
  font-size: 0.78rem;
  color: $text-placeholder;
}

/* ---------- Responsive ---------- */
@media (min-width: 480px) {
  .lp-form-inner {
    padding: 3rem 2.5rem;
  }

  .lp-logo-img {
    height: 108px;
    max-width: 340px;
  }

  .lp-title {
    font-size: 2rem;
  }
}
</style>
